/**
 * Single implementation behind every "Download" affordance for an attachment.
 *
 * Two surfaces (the AttachmentDetail page hook and the embedded AttachmentView)
 * had diverged: one re-fetched an attachment-disposition URL and clicked an
 * anchor, the other reused the cached inline-preview URL and called
 * `window.open` after an await. The second silently no-ops behind a popup
 * blocker (`window.open` outside the user-gesture window returns null without
 * throwing, so nothing is caught and no toast fires) and opens the file in a
 * tab rather than saving it.
 *
 * Throws on failure so callers can surface their own toast.
 */

import { apiService } from '../services/api.service';
import { getAttachmentUrl } from '../services/api/attachments.api';

export async function triggerAttachmentDownload(
  artifactId: string,
  fileName?: string,
): Promise<void> {
  // Always request a fresh URL with disposition=attachment — never reuse a
  // cached preview URL, which is the inline-preview URL and would open in a
  // browser tab (e.g. an audio player) instead of downloading.
  const result = await getAttachmentUrl(apiService, artifactId, 'attachment');
  // Validate the URL itself, not just the envelope: `href = undefined` stringifies
  // to "/undefined" and `href = ''` resolves to the current page, both same-origin,
  // so the `download` attribute would happily save the app's own HTML under the
  // attachment's filename — a silent wrong-file download with no toast.
  const url = result.success ? result.data?.url : undefined;
  if (typeof url !== 'string' || url.length === 0) {
    throw new Error(result.error?.message || 'Failed to get download URL');
  }

  downloadUrl(url, fileName || 'download');
}

/**
 * Saves a URL as a file. Anchor click (not window.open) so no blank tab flashes
 * and no popup blocker can swallow the action; the `download` attribute forces
 * a save for same-origin local-dev storage, and the baked-in
 * Content-Disposition handles the cross-origin GCS case. No `target`: the
 * download must never open a tab. `rel="noopener"` is inert while there is no
 * target; it is kept so the anchor stays safe if one is ever added.
 */
export function downloadUrl(url: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  // The anchor must be IN the document when clicked — Firefox ignores
  // `click()` on a detached anchor, which would silently no-op the download.
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    document.body.removeChild(link);
  }
}
